import { useEffect, useRef } from 'react';

/**
 * Next and Back copy a step's form into the stepper's state before the step unmounts. Clicking
 * another step's header unmounts it without either, which dropped the step's edits: they were
 * gone when the step was opened again and missing from a save made on another step.
 *
 * `commit` runs when the step unmounts, unless the step already committed on its way out.
 * Call the returned function right before Next or Back.
 */
type RegisterCommit = (commitNow: () => void) => () => void;

export const useCommitOnLeave = (commit: () => void, registerCommit?: RegisterCommit): (() => void) => {
  const latestCommit = useRef(commit);
  latestCommit.current = commit;
  const latestRegister = useRef(registerCommit);
  latestRegister.current = registerCommit;
  const committed = useRef(false);

  useEffect(() => {
    // StepContent keeps the old step mounted during its exit animation. A header click must
    // commit before the new step can save; cleanup remains a fallback for other unmounts.
    // A Next/Back transition may still have this component mounted when its header is
    // selected again. Always commit on a later header leave, even if Next/Back committed
    // an earlier version of the form.
    const commitNow = () => latestCommit.current();
    const unregister = latestRegister.current?.(commitNow);
    return () => {
      unregister?.();
      if (!committed.current) commitNow();
    };
  }, []);

  return () => {
    committed.current = true;
  };
};
